/**
 * Frontend extension manifest consumed by the platform build.
 *
 * The platform resolves `@afriannotate/extensions` to this package when
 * AFRI_EXTENSIONS_DIR points here, and to an empty stub otherwise, so
 * every list is optional.
 */
import { FINDER_PAGES } from "./finder/pages/finderRoutes";
import { finderApiEndpoints } from "./finder/apiEndpoints";
import { finderHostMode } from "./finder/hostMode";
import { reuseApiEndpoints } from "./reuse/apiEndpoints";
import { ReuseDataPanel } from "./reuse/ReuseDataPanel";
import { bringDataset } from "./reuse/finish";
import { SaveAsDataset } from "./reuse/SaveAsDataset";
import { DatasetsRoute } from "./reuse/routes";
import { accessApiEndpoints } from "./access/apiEndpoints";
import { SecurityCheckRoute, accessTab } from "./access/routes";
import { approvalsApiEndpoints } from "./approvals/apiEndpoints";
import { approvalsTab } from "./approvals/routes";
import { platformSignInTab, ssoTab } from "./sso/routes";
import { feedApiEndpoints } from "./feed/apiEndpoints";
import { insightsApiEndpoints } from "./insights/apiEndpoints";
import { insightsPanels } from "./insights/panels";
import { FeedSettingsPage } from "./feed/settingsPage";
import { feedSettingsIndex } from "./feed/settingsIndex";
import { releasesApiEndpoints } from "./releases/apiEndpoints";
import { ReleasesSettingsPage } from "./releases/settingsPage";
import { releasesSettingsIndex } from "./releases/settingsIndex";
import { trainingApiEndpoints } from "./training/apiEndpoints";
import { assistApiEndpoints } from "./assist/apiEndpoints";
import { assistTab, assistWizardPanel, platformAiTab } from "./assist/routes";

import { TrainingSettingsPage } from "./training/settingsPage";
import { trainingSettingsIndex } from "./training/settingsIndex";
import { ssoApiEndpoints } from "./sso/apiEndpoints";
import { payDetailsSection, payoutsPage as payPayoutsPage } from "./pay/routes";
import { payApiEndpoints } from "./pay/apiEndpoints";
import { auditApiEndpoints } from "./audit/apiEndpoints";
import { auditTab } from "./audit/routes";
import { bucketsApiEndpoints } from "./buckets/apiEndpoints";
import { bucketsIcon } from "./buckets/icon";
import { BucketsRoute } from "./buckets/routes";
import { datasetsIcon } from "./reuse/icon";
import { PreferenceExportCard } from "./preference/ExportCard";
import { PreferenceResultsButton } from "./preference/ResultsButton";
import { preferenceApiEndpoints } from "./preference/apiEndpoints";
import { preferenceFields } from "./preference/configFields";
import { preferenceSchema } from "./preference/configSchema";
import { elanConfigImport } from "./elan/configImport";
import { collectionApiEndpoints } from "./collection/apiEndpoints";
import { CollectionSettingsPage } from "./collection/settingsPage";
import { collectionSettingsIndex } from "./collection/settingsIndex";
import { SensitivityChip } from "./sensitivity/SensitivityChip";
import { sensitivityApiEndpoints } from "./sensitivity/apiEndpoints";
import { sensitivitySettingsIndex } from "./sensitivity/settingsIndex";
import { SensitivitySettingsPage } from "./sensitivity/settingsPage";
import { TrackPicker } from "./tracks/TrackPicker";
import { trackMemberColumn } from "./tracks/memberColumn";
import { tracksApiEndpoints } from "./tracks/apiEndpoints";
import { tracksSettingsIndex } from "./tracks/settingsIndex";
import { TracksSettingsPage } from "./tracks/settingsPage";
import { PromptsPanelLazy } from "./collection/promptsPanelLazy";
import { importsApiEndpoints } from "./imports/apiEndpoints";
import { ImportStepLazy } from "./imports/importStepLazy";
import { subscriptionsApiEndpoints } from "./subscriptions/apiEndpoints";
import { billingApiEndpoints } from "./billing/apiEndpoints";
import { platformMoneyTab } from "./billing/routes";
import { subscriptionTab } from "./subscriptions/routes";
import { platformSettingsBusinessTab } from "./subscriptions/BusinessTab";
import { insightsHintPanel, releasesHintCard } from "./subscriptions/Hints";

// What the person's organisation is on a subscription type for: the server
// says (APP_SETTINGS.extensions_on) and refuses the rest itself; here the
// pages, tabs and buttons of what is off are simply not offered. No list
// (an app shell loaded before the server answered): everything is offered.
const offered = (window as unknown as { APP_SETTINGS?: { extensions_on?: string[] } }).APP_SETTINGS?.extensions_on;
const on = (name: string) => !Array.isArray(offered) || offered.includes(name);

// Finder pages bring their own chrome (top bar + subnav), so the platform
// header and sidebar stay hidden on them in either host mode.
for (const page of FINDER_PAGES) {
  (page as unknown as { chromeless?: boolean }).chromeless = true;
}

// Sidebar entries. Datasets sits right under Projects.
export const navigation = [
  ...(on("reuse")
    ? [{ label: "Datasets", to: "/datasets", icon: datasetsIcon, when: "org_manager", after: "projects" }]
    : []),
  ...(on("buckets")
    ? [{ label: "Buckets", to: "/buckets", icon: bucketsIcon, when: "org_manager", after: "projects" }]
    : []),
];
export const routes = [
  ...FINDER_PAGES,
  ...(on("reuse") ? [DatasetsRoute] : []),
  ...(on("buckets") ? [BucketsRoute] : []),
  ...(on("access") ? [SecurityCheckRoute] : []),
];
// Tabs of the Organization page: governance sits with Members and Policies,
// not in the sidebar.
export const organizationTabs = [
  ...(on("access") ? [accessTab] : []),
  ...(on("sso") ? [ssoTab] : []),
  ...(on("approvals") ? [approvalsTab] : []),
  ...(on("audit") ? [auditTab] : []),
  ...(on("assist") ? [assistTab] : []),
  ...(on("subscriptions") ? [subscriptionTab] : []),
];
// Sending a payout run, on the organisation's Payouts page.
export const payoutsPage = on("pay") ? payPayoutsPage : undefined;
// Cards on a person's own Earnings.
export const earningsSections = on("pay") ? [payDetailsSection] : [];
export const settingsTabs = {
  project: {
    ...(on("collection") ? { CollectionSettingsPage } : {}),
    ...(on("tracks") ? { TracksSettingsPage } : {}),
    ...(on("sensitivity") ? { SensitivitySettingsPage } : {}),
    ...(on("training") ? { TrainingSettingsPage } : {}),
    ...(on("releases") ? { ReleasesSettingsPage } : {}),
    ...(on("feed") ? { FeedSettingsPage } : {}),
  },
  organization: [],
  platform: [platformMoneyTab, platformSignInTab, ...(on("assist") ? [platformAiTab] : [])],
  // Sections of Platform, Settings: the switch that starts the business.
  platformSettings: [platformSettingsBusinessTab],
};
// Panels beside the Data Manager's table, opened by a toolbar event.
export const dmPanels = on("collection")
  ? [{ id: "prompts", event: "promptsClicked", component: PromptsPanelLazy }]
  : [];
// Entries for the project settings search box.
export const settingsIndex = [
  ...(on("collection") ? collectionSettingsIndex : []),
  ...(on("tracks") ? tracksSettingsIndex : []),
  ...(on("sensitivity") ? sensitivitySettingsIndex : []),
  ...(on("training") ? trainingSettingsIndex : []),
  ...(on("releases") ? releasesSettingsIndex : []),
  ...(on("feed") ? feedSettingsIndex : []),
];
export const dashboardCards = [];
export const analyticsPanels = on("insights") ? insightsPanels : on("subscriptions") ? [insightsHintPanel] : [];
export const apiEndpoints = {
  ...finderApiEndpoints,
  ...reuseApiEndpoints,
  ...collectionApiEndpoints,
  ...importsApiEndpoints,
  ...preferenceApiEndpoints,
  ...tracksApiEndpoints,
  ...bucketsApiEndpoints,
  ...sensitivityApiEndpoints,
  ...auditApiEndpoints,
  ...accessApiEndpoints,
  ...ssoApiEndpoints,
  ...trainingApiEndpoints,
  ...assistApiEndpoints,
  ...releasesApiEndpoints,
  ...feedApiEndpoints,
  ...insightsApiEndpoints,
  ...payApiEndpoints,
  ...approvalsApiEndpoints,
  ...subscriptionsApiEndpoints,
  ...billingApiEndpoints,
};
// The Import step, in place of the platform's.
export const importStep = on("imports") ? { ImportPage: ImportStepLazy } : {};
// Extra tabs of the Import step.
export const importSources = on("reuse")
  ? [{ id: "reuse", label: "Dataset", component: ReuseDataPanel, finish: bringDataset }]
  : [];
// Buttons in a project's header on the Data Manager.
export const projectActions = [
  ...(on("sensitivity") ? [{ id: "sensitivity", component: SensitivityChip }] : []),
  ...(on("tracks") ? [{ id: "track-picker", component: TrackPicker }] : []),
  ...(on("preference") ? [{ id: "preference-results", component: PreferenceResultsButton }] : []),
  ...(on("reuse") ? [{ id: "save-as-dataset", component: SaveAsDataset }] : []),
];
export const hostModes = [finderHostMode];
export const memberColumns = on("tracks") ? [trackMemberColumn] : [];
// Cards on a project's Export page.
export const exportCards = [
  ...(on("preference") ? [{ id: "preference-judgements", component: PreferenceExportCard }] : []),
  ...(on("releases") || !on("subscriptions") ? [] : [releasesHintCard]),
];
// The task fields the extension's tags read, for the Import step.
export const configFields = on("preference") ? [preferenceFields] : [];
// Completion for the extension's tags in the labeling setup's code editor.
export const configSchema = on("preference") ? preferenceSchema : { tags: {}, inView: [] };
// Labeling configs built from a file, beside Browse Templates.
export const configImports = on("elan") ? [elanConfigImport] : [];
// A head start in the New Project wizard: a draft from a description.
export const wizardPanels = on("assist") ? [assistWizardPanel] : [];
